import { useEffect, useState } from 'react'
import type { AccountUse, Bill, FinanceSummary, FinancialAccount } from '../../types/family-finance'
import {
  billStatusOf,
  getFinanceSummary,
  listAccounts,
  listBills,
} from '../../lib/family-finance-api'
import {
  Card,
  Chip,
  LoadError,
  LoadingRows,
  errorMessage,
  ProgressBar,
  StatCard,
  daysUntil,
  money,
  relativeTime,
  shortDate,
  signedMoney,
} from './shared'

const TYPE_GROUPS: Array<{ label: string; types: string[] }> = [
  { label: 'Cash', types: ['checking', 'savings'] },
  { label: 'Investments', types: ['brokerage', 'retirement', '529', 'hsa'] },
  { label: 'Property', types: ['property'] },
  { label: 'Credit & Loans', types: ['credit', 'mortgage'] },
]

type UseFilter = 'all' | AccountUse

const USE_FILTERS: Array<{ id: UseFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'personal', label: 'Personal' },
  { id: 'business', label: 'Business' },
]

export default function OverviewTab({ onGoToTab }: { onGoToTab: (tab: string) => void }) {
  const [summary, setSummary] = useState<FinanceSummary | null>(null)
  const [accounts, setAccounts] = useState<FinancialAccount[]>([])
  const [bills, setBills] = useState<Bill[]>([])
  const [useFilter, setUseFilter] = useState<UseFilter>('all')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    Promise.all([getFinanceSummary(), listAccounts(), listBills()])
      .then(([s, a, b]) => {
        if (cancelled) return
        setSummary(s)
        setAccounts(a)
        setBills(b)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(errorMessage(err))
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  if (loadError) return <LoadError error={loadError} onRetry={() => setReloadKey((k) => k + 1)} />
  if (loading || !summary) return <LoadingRows rows={5} />

  const upcoming = bills
    .filter((b) => billStatusOf(b) !== 'paid' && daysUntil(b.nextDueDate) <= 21)
    .slice(0, 6)
  const needsAttention = bills.filter(
    (b) => !b.autopay && ['due-soon', 'overdue'].includes(billStatusOf(b)),
  )
  const scoped = useFilter === 'all' ? accounts : accounts.filter((a) => a.use === useFilter)

  return (
    <div className="space-y-6">
      {/* Headline stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Net Worth"
          value={money(summary.netWorth)}
          sub={`${signedMoney(summary.netWorthChange30d)} last 30 days`}
          subTone={summary.netWorthChange30d >= 0 ? 'green' : 'red'}
        />
        <StatCard label="Total Assets" value={money(summary.totalAssets)} sub={`Investable: ${money(summary.investableAssets)}`} />
        <StatCard label="Total Liabilities" value={money(summary.totalLiabilities)} sub="Mortgage + cards" subTone="slate" />
        <StatCard
          label="Bills — Next 14 Days"
          value={money(summary.billsDueNext14Days.total)}
          sub={`${summary.billsDueNext14Days.count} bills scheduled`}
          subTone={needsAttention.length > 0 ? 'amber' : 'slate'}
        />
      </div>

      {/* Alerts */}
      {needsAttention.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
          <p className="text-amber-600 dark:text-amber-400 text-sm font-medium mb-1">
            ⏰ {needsAttention.length} bill{needsAttention.length > 1 ? 's' : ''} without autopay due soon
          </p>
          <p className="text-amber-600/70 dark:text-amber-400/70 text-xs">
            {needsAttention.map((b) => `${b.name} (${shortDate(b.nextDueDate)})`).join(' · ')}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Balances by group */}
        <Card
          title="Balances"
          subtitle={`Last sync ${relativeTime(summary.lastSync)}`}
          action={
            <button onClick={() => onGoToTab('accounts')} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm">
              Manage →
            </button>
          }
        >
          {/* Household and business money sit in the same institutions, so the
              groups below are only readable once you pick which you're looking at. */}
          <div className="flex gap-1 mb-4">
            {USE_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setUseFilter(f.id)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  useFilter === f.id
                    ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="space-y-4">
            {TYPE_GROUPS.map((group) => {
              const rows = scoped.filter((a) => group.types.includes(a.type))
              if (rows.length === 0) return null
              const total = rows.reduce((s, a) => s + a.balance, 0)
              return (
                <div key={group.label}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">{group.label}</p>
                    <p className={`text-sm font-semibold tabular-nums ${total < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>
                      {money(total)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    {rows.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
                        <span className="text-slate-600 dark:text-slate-300 truncate mr-3">
                          {a.name} <span className="text-slate-500">·· {a.mask}</span>
                        </span>
                        <span className={`tabular-nums ${a.balance < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-200'}`}>{money(a.balance)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
            {scoped.length === 0 && (
              <p className="text-slate-500 dark:text-slate-400 text-sm py-2">
                No {useFilter} accounts yet — tag them on the Accounts tab.
              </p>
            )}
          </div>
        </Card>

        <div className="space-y-6">
          {/* Budget snapshot */}
          <Card
            title={`Budget — ${summary.budgetMonth}`}
            action={
              <button onClick={() => onGoToTab('budget')} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm">
                Details →
              </button>
            }
          >
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{money(summary.budgetSpent)}</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm">of {money(summary.budgetTotal)} budgeted</p>
            </div>
            <ProgressBar
              value={summary.budgetSpent}
              max={summary.budgetTotal}
              tone={summary.budgetSpent > summary.budgetTotal ? 'red' : 'blue'}
            />
            <p className="text-slate-500 dark:text-slate-400 text-xs mt-2">
              {money(Math.max(0, summary.budgetTotal - summary.budgetSpent))} remaining this month
            </p>
          </Card>

          {/* Upcoming bills */}
          <Card
            title="Upcoming Bills"
            action={
              <button onClick={() => onGoToTab('bills')} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm">
                All bills →
              </button>
            }
          >
            <div className="space-y-2">
              {upcoming.map((b) => {
                const status = billStatusOf(b)
                const days = daysUntil(b.nextDueDate)
                return (
                  <div key={b.id} className="flex items-center justify-between py-1.5 border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800 dark:text-slate-200 truncate">{b.name}</p>
                      <p className="text-xs text-slate-500">
                        {shortDate(b.nextDueDate)} · {days <= 0 ? 'today' : `in ${days}d`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {b.autopay ? (
                        <Chip tone="green">autopay</Chip>
                      ) : status === 'overdue' ? (
                        <Chip tone="red">overdue</Chip>
                      ) : status === 'due-soon' ? (
                        <Chip tone="amber">due soon</Chip>
                      ) : null}
                      <span className="text-sm text-slate-900 dark:text-white font-medium">{money(b.amount, 2)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
